import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { UserSidebar } from '@/components/Layout/UserSidebar';
import { projectService } from '@/services/projectService';
import { areaService } from '@/services/areaService';
import { Area, AttributeType } from '@/types';
import { logementsService, LogementsSupervisionStatus } from '@/services/logementsService';
import { streamService, Stream } from '@/services/streamService';
import { attributeService } from '@/services/attributeService';
import { DateTimePicker } from '@/components/DateTimePicker';
import { computeChartRange, sanitizeStreamSeriesValues } from '@/utils/chartRanges';
import { formatParisDateTime, formatLocalDateTimeForPicker, localPickerValueToIso, parseApiDateTime } from '@/utils/dateTime';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/authStore';
import { useTheme } from '@/hooks/useTheme';
import { ProjectLogo } from '@/components/ProjectLogo';

type StatusFilter = 'ALL' | 'ONLINE' | 'OFFLINE';

type ApartmentSupervision = {
  apartment: { id: number; name: string; code: string | null; number: string | null };
  floor: { id: number; name: string; code: string | null };
  building: { id: number; name: string; code: string | null };
  box: { id: number | null; name: string | null; isOnline: boolean | null };
  thermostat: { isOnline: boolean | null; enabled: boolean; modeLabel: string };
  metrics: {
    tempInterieur: number | null;
    tempInterieurOutOfRange: boolean;
    tempInterieurAttrId: number | null;
    tempConsigne: number | null;
    tempConsigneAttrId: number | null;
    humidity: number | null;
    humidityAttrId: number | null;
    position: 'Ouverte' | 'Fermée';
    operatingStateAttrId: number | null;
    manoeuvres: number | null;
  };
};

export function LogementsPage() {
  const { logout } = useAuth();
  const { isRoot, isAdmin } = useAuthStore();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const projectIdInt = projectId ? Number(projectId) : null;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);
  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAttributeId, setModalAttributeId] = useState<number | null>(null);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [modalInstance, setModalInstance] = useState(0);
  const [modalApartment, setModalApartment] = useState<ApartmentSupervision | null>(null);

  // Fetch user's projects and automatically select the first one
  const { data: projectsResponse, isLoading: isLoadingProjects } = useQuery({
    queryKey: ['user-projects'],
    queryFn: () => projectService.getProjects({ page: 1, itemsPerPage: 100 }),
  });

  const userProjects = projectsResponse?.data || [];
  const currentProject = useMemo(() => {
    if (!projectIdInt) return null;
    return userProjects.find((p) => p.id === projectIdInt) ?? null;
  }, [userProjects, projectIdInt]);

  if (!projectIdInt) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <span className="material-icons-outlined text-red-500 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Projet requis</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-4">Veuillez sélectionner un projet pour accéder aux logements.</p>
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Choisir un projet
            </button>
          </div>
        </div>
      </div>
    );
  }

  const {
    data: buildingsData,
    isLoading: isLoadingBuildings,
    error: buildingsError,
  } = useQuery({
    queryKey: ['logements', currentProject?.id, 'buildings'],
    queryFn: async () => {
      if (!currentProject?.id) return [];
      // Use optimized endpoint that doesn't load relations
      return await areaService.getAreasTree(currentProject.id);
    },
    enabled: !!currentProject?.id,
  });

  const buildings = useMemo(() => buildingsData ?? [], [buildingsData]);

  // Redirection admin/root sans projet (évite setState pendant le render)
  const shouldRedirectAdmin = !currentProject && projectIdInt && (isRoot() || isAdmin());
  useEffect(() => {
    if (shouldRedirectAdmin) {
      navigate(isRoot() ? '/admin/dashboard' : '/admin/users', { replace: true });
    }
  }, [shouldRedirectAdmin, isRoot, isAdmin, navigate]);

  // Default building selection when list loads
  useEffect(() => {
    if (selectedBuildingId === null && buildings.length > 0) {
      setSelectedBuildingId(buildings[0].id);
    }
  }, [buildings, selectedBuildingId]);

  const selectedBuilding = useMemo(() => {
    if (!selectedBuildingId) return null;
    return buildings.find((b) => b.id === selectedBuildingId) ?? null;
  }, [buildings, selectedBuildingId]);

  const {
    data: floorsData,
    isLoading: isLoadingFloors,
    error: floorsError,
  } = useQuery({
    queryKey: ['logements', currentProject?.id, 'building', selectedBuildingId, 'floors'],
    queryFn: async () => {
      if (!currentProject?.id || !selectedBuildingId) return [];
      // Use optimized endpoint that doesn't load relations
      return await areaService.getAreasTree(currentProject.id, selectedBuildingId);
    },
    enabled: !!currentProject?.id && !!selectedBuildingId,
  });

  const floors = useMemo(() => floorsData ?? [], [floorsData]);

  // Default floor selection when building/floor list loads
  useEffect(() => {
    if (!selectedBuildingId) return;
    if (floors.length === 0) {
      setSelectedFloorId(null);
      return;
    }
    // If current floor isn't part of current building floors, select first
    if (selectedFloorId === null || !floors.some((f) => f.id === selectedFloorId)) {
      setSelectedFloorId(floors[0].id);
    }
  }, [floors, selectedBuildingId, selectedFloorId]);

  const apiStatus: LogementsSupervisionStatus = useMemo(() => {
    if (statusFilter === 'ONLINE') return 'online';
    if (statusFilter === 'OFFLINE') return 'offline';
    return 'all';
  }, [statusFilter]);

  const {
    data: supervisionData,
    isLoading: isLoadingSupervision,
    error: supervisionError,
  } = useQuery({
    queryKey: ['logements', currentProject?.id, 'supervision', selectedBuildingId, selectedFloorId, apiStatus],
    queryFn: async () => {
      if (!currentProject?.id) return null;
      return await logementsService.getSupervision({
        projectId: currentProject.id,
        buildingId: selectedBuildingId,
        floorId: selectedFloorId,
        status: apiStatus,
        page: 1,
        itemsPerPage: 500,
      });
    },
    enabled: !!currentProject?.id && !!selectedBuildingId && !!selectedFloorId,
  });

  const supervisionItems: ApartmentSupervision[] = useMemo(() => {
    return (supervisionData?.items ?? []) as ApartmentSupervision[];
  }, [supervisionData]);

  const filteredFloors = useMemo(() => {
    if (!selectedBuilding) return [];
    const byFloor = new Map<number, { floor: { id: number; name: string }; apartments: ApartmentSupervision[] }>();
    for (const item of supervisionItems) {
      if (item.building.id !== selectedBuilding.id) continue;
      if (selectedFloorId && item.floor.id !== selectedFloorId) continue;
      const existing = byFloor.get(item.floor.id);
      if (existing) {
        existing.apartments.push(item);
      } else {
        byFloor.set(item.floor.id, {
          floor: { id: item.floor.id, name: item.floor.name },
          apartments: [item],
        });
      }
    }
    return Array.from(byFloor.values()).sort((a, b) => a.floor.name.localeCompare(b.floor.name));
  }, [selectedBuilding, supervisionItems]);

  // Calculate statistics from supervisionItems
  const statistics = useMemo(() => {
    const totalLogements = supervisionItems.length;
    
    // Deduplicate boxes by ID (a box can be associated with multiple apartments)
    const uniqueBoxes = new Map<number, { id: number; isOnline: boolean | null }>();
    supervisionItems.forEach((item) => {
      if (item.box.id !== null && !uniqueBoxes.has(item.box.id)) {
        uniqueBoxes.set(item.box.id, {
          id: item.box.id,
          isOnline: item.box.isOnline,
        });
      }
    });
    const boxesOffline = Array.from(uniqueBoxes.values()).filter((box) => box.isOnline === false).length;
    
    const thermostatsOffline = supervisionItems.filter((item) => item.thermostat.isOnline === false).length;
    const temperatures = supervisionItems
      .map((item) => item.metrics.tempInterieur)
      .filter((temp): temp is number => temp !== null);
    const maxTemp = temperatures.length > 0 ? Math.max(...temperatures) : null;
    const minTemp = temperatures.length > 0 ? Math.min(...temperatures) : null;
    // Values are already clamped to [7, 36]°C server-side; flag when a displayed extreme
    // came from a raw sensor reading outside that plausible range (likely a sensor fault).
    const maxTempOutOfRange = supervisionItems.some(
      (item) => item.metrics.tempInterieur === maxTemp && item.metrics.tempInterieurOutOfRange
    );
    const minTempOutOfRange = supervisionItems.some(
      (item) => item.metrics.tempInterieur === minTemp && item.metrics.tempInterieurOutOfRange
    );

    return {
      totalLogements,
      boxesOffline,
      thermostatsOffline,
      maxTemp,
      minTemp,
      maxTempOutOfRange,
      minTempOutOfRange,
    };
  }, [supervisionItems]);

  // Loading state
  if (isLoadingProjects) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-icons-outlined animate-spin text-primary text-6xl mb-4">progress_activity</span>
            <p className="text-slate-500 dark:text-slate-400">Chargement des projets...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentProject) {
    if (isRoot() || isAdmin()) {
      return (
        <div className="flex h-screen bg-slate-50 dark:bg-slate-900 items-center justify-center">
          <span className="material-icons-outlined animate-spin text-primary text-4xl">progress_activity</span>
        </div>
      );
    }
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-icons-outlined text-slate-400 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Aucun projet assigné</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-6">Vous n'avez pas encore accès à un projet. Contactez votre administrateur pour obtenir un accès.</p>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 rounded-lg transition-colors"
            >
              <span className="material-icons-outlined">logout</span>
              Se déconnecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  const StatusBadge = ({ online, label }: { online: boolean; label: string }) => (
    <span
      className={`px-2 py-1 rounded-md text-xs font-semibold ${
        online ? 'bg-green-500 text-white' : 'bg-slate-300 dark:bg-slate-600 text-slate-700 dark:text-slate-200'
      }`}
    >
      {label}
    </span>
  );

  const ValuePill = ({ children }: { children: React.ReactNode }) => (
    <span className="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 px-2 py-1 rounded-md text-xs font-semibold">{children}</span>
  );

  const MetricValue = ({ value, unit }: { value: string; unit?: string }) => (
    <span className="bg-slate-200/60 dark:bg-slate-700/60 text-slate-700 dark:text-slate-200 px-2 py-0.5 rounded-md text-xs font-semibold inline-flex items-center gap-1">
      <span className="text-slate-900 dark:text-slate-100">{value}</span>
      {unit ? <span className="text-slate-600 dark:text-slate-300">{unit}</span> : null}
      <span className="material-icons-outlined text-pink-300" style={{ fontSize: '16px' }}>
        trending_up
      </span>
    </span>
  );

  const MiniSparkline = ({ colorClass }: { colorClass: string }) => (
    <div className="h-4 w-full mt-2 relative overflow-hidden rounded-md bg-slate-200/40 dark:bg-slate-800/40 border border-slate-300/60 dark:border-slate-700/60 hidden md:block">
      <svg viewBox="0 0 120 32" className="absolute inset-0 h-full w-full">
        <polyline
          fill="none"
          className={colorClass}
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          points="0,26 10,24 20,22 30,19 40,21 50,18 60,16 70,17 80,14 90,15 100,13 110,12 120,10"
        />
      </svg>
      <div className={`absolute inset-0 ${colorClass} opacity-10`} />
    </div>
  );

  const MetricCard = ({
    icon,
    title,
    value,
    unit,
    badge,
    badgeTone = 'slate',
    accent = 'cyan',
    showSparkline = false,
    onClick,
  }: {
    icon: string;
    title: string;
    value: string;
    unit?: string;
    badge?: string;
    badgeTone?: 'green' | 'red' | 'slate' | 'yellow' | 'purple' | 'cyan';
    accent?: 'yellow' | 'cyan' | 'purple' | 'emerald' | 'orange';
    showSparkline?: boolean;
    onClick?: () => void;
  }) => {
    const badgeClass =
      badgeTone === 'green'
        ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 border-emerald-500/20'
        : badgeTone === 'red'
          ? 'bg-rose-500/15 text-rose-700 dark:text-rose-200 border-rose-500/20'
          : badgeTone === 'yellow'
            ? 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-200 border-yellow-500/20'
            : badgeTone === 'purple'
              ? 'bg-purple-500/15 text-purple-700 dark:text-purple-200 border-purple-500/20'
              : badgeTone === 'cyan'
                ? 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-200 border-cyan-500/20'
                : 'bg-slate-500/10 text-slate-600 dark:text-slate-200 border-slate-500/10';

    const iconBg =
      accent === 'yellow'
        ? 'bg-yellow-500/15 text-yellow-300 border-yellow-500/20'
        : accent === 'purple'
          ? 'bg-purple-500/15 text-purple-300 border-purple-500/20'
          : accent === 'emerald'
            ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20'
            : accent === 'orange'
              ? 'bg-orange-500/15 text-orange-300 border-orange-500/20'
              : 'bg-cyan-500/15 text-cyan-300 border-cyan-500/20';

    const sparkColor =
      accent === 'yellow'
        ? 'stroke-yellow-400'
        : accent === 'purple'
          ? 'stroke-purple-400'
          : accent === 'emerald'
            ? 'stroke-emerald-400'
            : accent === 'orange'
              ? 'stroke-orange-400'
              : 'stroke-cyan-400';

    return (
      <div
        className={`relative rounded-2xl border border-slate-200 dark:border-slate-700/70 bg-white dark:bg-slate-900/70 shadow-sm p-2 min-w-0 ${
          onClick ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/80 transition-colors' : ''
        }`}
        onClick={onClick}
        title={onClick ? 'Cliquer pour voir le graphique' : undefined}
      >
        <div className="flex items-start justify-between gap-2 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`h-8 w-8 rounded-2xl border flex items-center justify-center ${iconBg} shrink-0`}>
              <span className="material-icons-outlined" style={{ fontSize: '16px' }}>
                {icon}
              </span>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] text-slate-600 dark:text-slate-300 font-semibold truncate leading-tight">{title}</div>
              <div className="mt-0.5 flex items-baseline gap-1.5 min-w-0">
                <div
                  className={`font-extrabold tracking-tight text-slate-900 dark:text-white leading-none ${
                    value.length > 8 ? 'text-base' : value.length > 5 ? 'text-lg' : 'text-xl'
                  } truncate max-w-[140px]`}
                  title={value}
                >
                  {value}
                </div>
                {unit ? <div className="text-xs text-slate-500 dark:text-slate-400 font-semibold shrink-0">{unit}</div> : null}
              </div>
            </div>
          </div>
          {badge ? (
            <span
              className={`shrink-0 px-2 py-0.5 rounded-lg border text-[10px] font-bold leading-none ${badgeClass} max-w-[120px] truncate`}
              title={badge}
            >
              {badge}
            </span>
          ) : null}
        </div>
        {showSparkline && <MiniSparkline colorClass={sparkColor} />}
      </div>
    );
  };

  const ApartmentRow = ({ item }: { item: ApartmentSupervision }) => {
    const boxOnline = item.box.isOnline ?? false;
    const thermostatOnline = item.thermostat.isOnline ?? false;
    const tempInterieur = item.metrics.tempInterieur;
    const tempConsigne = item.metrics.tempConsigne;
    const humidity = item.metrics.humidity;
    const manoeuvres = item.metrics.manoeuvres ?? 0;
    const vanneOuverte = item.metrics.position === 'Ouverte';

    const openModal = (attrId: number | null, title: string) => {
      if (!attrId) return;
      setModalApartment(item);
      setModalAttributeId(attrId);
      setModalTitle(title);
      setModalOpen(true);
      setModalInstance((v) => v + 1);
    };

    const openModalWithTempInterieur = () => {
      const attrId = item.metrics.tempInterieurAttrId ?? item.metrics.tempConsigneAttrId ?? item.metrics.humidityAttrId ?? item.metrics.operatingStateAttrId;
      const title = item.metrics.tempInterieurAttrId ? 'Temp. intérieur' : item.metrics.tempConsigneAttrId ? 'Temp. consigne' : item.metrics.humidityAttrId ? 'Humidité' : 'Vanne radiateur';
      openModal(attrId, title);
    };

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={openModalWithTempInterieur}
        onKeyDown={(e) => e.key === 'Enter' && openModalWithTempInterieur()}
        className={`grid grid-cols-12 gap-0 rounded-xl border text-sm transition-colors cursor-pointer ${
          !boxOnline
            ? 'border-rose-300 dark:border-rose-800/40 bg-rose-50 dark:bg-rose-950/20'
            : 'border-slate-200 dark:border-slate-700/60 bg-white dark:bg-slate-800/30 hover:bg-slate-50 dark:hover:bg-slate-800/50'
        }`}
      >
        {/* Nom logement */}
        <div className="col-span-3 flex items-center gap-3 px-4 py-3 border-r border-slate-200 dark:border-slate-700/40 min-w-0">
          <div className={`w-2 h-2 rounded-full shrink-0 ${boxOnline ? 'bg-emerald-400' : 'bg-rose-500'}`} />
          <span className="font-bold text-slate-900 dark:text-white truncate">{item.apartment.name}</span>
        </div>

        {/* Box */}
        <div className="col-span-2 flex items-center gap-2 px-4 py-3 border-r border-slate-200 dark:border-slate-700/40 min-w-0">
          <span className="material-icons-outlined text-slate-400 shrink-0" style={{ fontSize: '16px' }}>dns</span>
          <span className={`text-xs font-semibold truncate ${boxOnline ? 'text-emerald-500 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400'}`}>
            {item.box.name ?? 'N/A'}
          </span>
        </div>

        {/* Thermostat / Chauffage */}
        <div className="col-span-2 flex items-center gap-2 px-4 py-3 border-r border-slate-200 dark:border-slate-700/40 min-w-0">
          <span className="material-icons-outlined text-orange-400 shrink-0" style={{ fontSize: '16px' }}>thermostat</span>
          <div className="min-w-0">
            <span className={`text-xs font-bold ${thermostatOnline ? 'text-emerald-500 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}>
              {thermostatOnline ? 'En ligne' : 'Hors ligne'}
            </span>
            <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
              {item.thermostat.enabled
                ? item.thermostat.modeLabel.includes('Eco') ? 'Eco' : 'Chauffe'
                : 'OFF'}
            </span>
          </div>
        </div>

        {/* Températures */}
        <div className="col-span-3 flex items-center gap-3 px-4 py-3 border-r border-slate-200 dark:border-slate-700/40">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openModal(item.metrics.tempInterieurAttrId, 'Temp. intérieur');
            }}
            className={`flex items-center gap-1.5 group ${item.metrics.tempInterieurAttrId ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <span className="material-icons-outlined text-orange-400 shrink-0" style={{ fontSize: '14px' }}>home</span>
            <span className={`font-bold text-slate-900 dark:text-white ${item.metrics.tempInterieurAttrId ? 'group-hover:text-orange-500 dark:group-hover:text-orange-300' : ''}`}>
              {tempInterieur !== null ? `${tempInterieur.toFixed(1)}°C` : '—'}
            </span>
          </button>
          <span className="text-slate-300 dark:text-slate-600">|</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openModal(item.metrics.tempConsigneAttrId, 'Temp. consigne');
            }}
            className={`flex items-center gap-1.5 group ${item.metrics.tempConsigneAttrId ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <span className="material-icons-outlined text-yellow-400 shrink-0" style={{ fontSize: '14px' }}>tune</span>
            <span className={`font-semibold text-slate-600 dark:text-slate-300 ${item.metrics.tempConsigneAttrId ? 'group-hover:text-yellow-500 dark:group-hover:text-yellow-300' : ''}`}>
              {tempConsigne !== null ? `${tempConsigne.toFixed(1)}°C` : '—'}
            </span>
          </button>
        </div>

        {/* Humidité */}
        <div className="col-span-1 flex items-center gap-1.5 px-4 py-3 border-r border-slate-200 dark:border-slate-700/40">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openModal(item.metrics.humidityAttrId, 'Humidité');
            }}
            className={`flex items-center gap-1.5 group ${item.metrics.humidityAttrId ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <span className="material-icons-outlined text-purple-400 shrink-0" style={{ fontSize: '14px' }}>water_drop</span>
            <span className={`font-semibold text-slate-600 dark:text-slate-300 ${item.metrics.humidityAttrId ? 'group-hover:text-purple-500 dark:group-hover:text-purple-300' : ''}`}>
              {humidity !== null ? `${humidity.toFixed(0)}%` : '—'}
            </span>
          </button>
        </div>

        {/* Vanne */}
        <div className="col-span-1 flex items-center gap-1.5 px-4 py-3">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openModal(item.metrics.operatingStateAttrId, 'Vanne radiateur');
            }}
            className={`flex items-center gap-1.5 group ${item.metrics.operatingStateAttrId ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <span className={`material-icons-outlined shrink-0 ${vanneOuverte ? 'text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`} style={{ fontSize: '14px' }}>
              {vanneOuverte ? 'radio_button_checked' : 'radio_button_unchecked'}
            </span>
            <span className={`text-xs font-semibold ${vanneOuverte ? 'text-emerald-500 dark:text-emerald-400 group-hover:text-emerald-400 dark:group-hover:text-emerald-300' : 'text-slate-400 dark:text-slate-500'}`}>
              {vanneOuverte ? `${manoeuvres}` : '—'}
            </span>
          </button>
        </div>
      </div>
    );
  };

  // Stream Modal Component
  function StreamModal({
    attributeId,
    title,
    onClose,
    apartment,
    onAttributeChange,
    projectId,
  }: {
    attributeId: number;
    title: string;
    onClose: () => void;
    apartment: ApartmentSupervision | null;
    onAttributeChange: (attrId: number, attrTitle: string) => void;
    projectId: number;
  }) {
    const queryClient = useQueryClient();
    
    // Default: last 48 hours - réinitialisé à chaque changement d'attribut
    const getDefaultDates = () => {
      const defaultTo = new Date();
      const defaultFrom = new Date(defaultTo.getTime() - 48 * 60 * 60 * 1000);
      return {
        from: formatLocalDateTimeForPicker(defaultFrom),
        to: formatLocalDateTimeForPicker(defaultTo),
      };
    };
    
    const [dateFrom, setDateFrom] = useState<string>(getDefaultDates().from);
    const [dateTo, setDateTo] = useState<string>(getDefaultDates().to);
    const [dateError, setDateError] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    const [fetchReady, setFetchReady] = useState(false);

    // Délai avant d'activer la requête pour éviter double fetch (remount rapide)
    useEffect(() => {
      setFetchReady(false);
      const t = setTimeout(() => setFetchReady(true), 50);
      return () => clearTimeout(t);
    }, [attributeId, dateFrom, dateTo, dateError]);

    // Validation des dates
    const validateDates = (from: string, to: string): string | null => {
      if (!from || !to) {
        return null; // Pas d'erreur si les dates ne sont pas encore sélectionnées
      }

      const fromDate = new Date(from);
      const toDate = new Date(to);

      if (fromDate >= toDate) {
        return 'La date de début doit être antérieure à la date de fin';
      }

      const diffHours = (toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60);
      if (diffHours < 24) {
        return 'La période doit être d\'au moins 24 heures';
      }

      return null;
    };

    const handleDateFromChange = (value: string) => {
      setDateFrom(value);
      const error = validateDates(value, dateTo);
      setDateError(error);
    };

    const handleDateToChange = (value: string) => {
      setDateTo(value);
      const error = validateDates(dateFrom, value);
      setDateError(error);
    };

    const fromISO = useMemo(() => {
      if (!dateFrom || dateError) return undefined;
      return localPickerValueToIso(dateFrom);
    }, [dateFrom, dateError]);

    const toISO = useMemo(() => {
      if (!dateTo || dateError) return undefined;
      const [datePart, timePart] = dateTo.split('T');
      const [hours = 0, minutes = 0] = (timePart || '').split(':').map(Number);
      if (hours === 0 && minutes === 0) {
        return localPickerValueToIso(`${datePart}T23:59`, { inclusiveEnd: true });
      }
      return localPickerValueToIso(dateTo, { inclusiveEnd: true });
    }, [dateTo, dateError]);

    const handleExport = async () => {
      if (!fromISO || !toISO || dateError) {
        return;
      }

      if (!projectId) {
        alert('Erreur : Projet non défini. Veuillez réessayer.');
        return;
      }

      setIsExporting(true);
      setExportProgress(0);

      // Simulate progress over 3 seconds (100 steps of 30ms each = 3 seconds)
      let simulatedProgress = 0;
      const progressInterval = setInterval(() => {
        simulatedProgress += 1;
        const progressPercent = Math.min(90, (simulatedProgress / 100) * 90); // Stop at 90% until download starts
        setExportProgress(progressPercent);
        
        if (simulatedProgress >= 100) {
          clearInterval(progressInterval);
        }
      }, 30); // 30ms * 100 = 3 seconds

      try {
        // Wait 3 seconds before starting the actual export
        await new Promise((resolve) => setTimeout(resolve, 3000));
        
        // Clear simulation interval
        clearInterval(progressInterval);
        setExportProgress(90);

        const blob = await streamService.exportCsv(
          projectId,
          attributeId,
          fromISO,
          toISO,
          (progress) => {
            // Use actual progress from XHR if available (usually not for blob downloads)
            if (progress > 90) {
              setExportProgress(progress);
            }
          }
        );

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Format filename: "nom appartement"_"nom attribute"_"date début"_"date fin".csv
        const sanitizeFilename = (str: string) => {
          return str
            .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filename characters
            .replace(/\s+/g, '_') // Replace spaces with underscores
            .trim();
        };
        
        const formatDateForFilename = (dateStr: string) => {
          // Convert "2026-01-12T22:51" to "2026-01-12_22-51"
          return dateStr.replace('T', '_').replace(/:/g, '-');
        };
        
        const apartmentName = apartment?.apartment.name || 'Appartement';
        const attributeName = title || 'Attribut';
        const dateFromFormatted = formatDateForFilename(dateFrom);
        const dateToFormatted = formatDateForFilename(dateTo);
        
        const filename = `${sanitizeFilename(apartmentName)}_${sanitizeFilename(attributeName)}_${dateFromFormatted}_${dateToFormatted}.csv`;
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setExportProgress(100);
        setTimeout(() => {
          setIsExporting(false);
          setExportProgress(0);
        }, 500);
      } catch (error: any) {
        clearInterval(progressInterval);
        console.error('Export error:', error);
        setIsExporting(false);
        setExportProgress(0);
        const errorMessage = error?.message || 'Erreur lors de l\'export. Veuillez réessayer.';
        alert(`Erreur lors de l'export: ${errorMessage}`);
      }
    };

    // Fetch attribute to get unit
    const { data: attribute } = useQuery({
      queryKey: ['attribute', attributeId],
      queryFn: () => attributeService.getAttribute(attributeId),
      enabled: !!attributeId,
    });

    const unit = useMemo(() => {
      if (!attribute) return '';
      const attributeType = typeof attribute.type === 'object' ? attribute.type : null;
      return (attributeType as AttributeType)?.unit || '';
    }, [attribute]);

    const attributeTypeCode = useMemo(() => {
      if (!attribute) return null;
      const attributeType = typeof attribute.type === 'object' ? attribute.type : null;
      return (attributeType as AttributeType)?.code || null;
    }, [attribute]);

    // Delay enabled to avoid double fetch on rapid mount/unmount (e.g. Strict Mode in dev)
    const [streamsEnabled, setStreamsEnabled] = useState(false);
    useEffect(() => {
      const t = setTimeout(() => setStreamsEnabled(true), 0);
      return () => clearTimeout(t);
    }, [attributeId, fromISO, toISO]);

    const { data: streams, isLoading, refetch } = useQuery({
      queryKey: ['streams', attributeId, fromISO, toISO],
      queryFn: () =>
        streamService.getAllStreamsByAttribute(attributeId, {
          itemsPerPage: 500,
          order: { timestamp: 'ASC' },
          from: fromISO,
          to: toISO,
        }),
      enabled: streamsEnabled && !!attributeId && !dateError && !!fromISO && !!toISO,
      refetchOnMount: false,
      retry: false,
    });

    const chartData = useMemo(() => {
      const buildFromStreams = () => {
        if (!streams || streams.length === 0) return [];
        const rawValues = streams.map((s) => parseFloat(s.value));
        const sanitizedValues = sanitizeStreamSeriesValues(rawValues, title, unit, attributeTypeCode);
        return streams
          .map((s, i) => ({
            timestamp: parseApiDateTime(s.timestamp).getTime(),
            value: sanitizedValues[i],
            label: formatParisDateTime(s.timestamp, {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            }),
          }))
          .filter((d) => Number.isFinite(d.value));
      };

      const fromStreams = buildFromStreams();
      if (fromStreams.length > 0) {
        return fromStreams;
      }

      // Fallback: show at least the latest current_value when history is empty in range
      if (attribute?.currentValue != null && attribute.currentValue !== '' && attribute.currentValueTs) {
        const raw = parseFloat(String(attribute.currentValue));
        if (Number.isFinite(raw)) {
          const sanitized = sanitizeStreamSeriesValues([raw], title, unit, attributeTypeCode)[0];
          if (Number.isFinite(sanitized)) {
            return [
              {
                timestamp: parseApiDateTime(attribute.currentValueTs).getTime(),
                value: sanitized,
                label: formatParisDateTime(attribute.currentValueTs, {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                }),
              },
            ];
          }
        }
      }

      return [];
    }, [attribute, attributeTypeCode, streams, title, unit]);

    const { min: minValue, max: maxValue, range, dataMin, dataMax } = computeChartRange(
      chartData.map((d) => d.value),
      title,
      unit,
      attributeTypeCode
    );
    const chartHeight = 300;
    const chartWidth = 800;
    const padding = 40;
    const leftPadding = 70; // Extra padding for Y-axis labels

    const [hoverIndex, setHoverIndex] = useState<number | null>(null);

    const getX = (index: number) => leftPadding + (index / (chartData.length - 1 || 1)) * (chartWidth - leftPadding - padding);
    const getY = (value: number) => {
      const clamped = Math.max(minValue, Math.min(maxValue, value));
      return chartHeight - padding - ((clamped - minValue) / range) * (chartHeight - 2 * padding);
    };

    const pathData =
      chartData.length > 0
        ? chartData
            .map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(d.value)}`)
            .join(' ')
        : '';

    const hoveredPoint = useMemo(() => {
      if (hoverIndex === null) return null;
      const p = chartData[hoverIndex];
      if (!p) return null;
      return {
        ...p,
        x: getX(hoverIndex),
        y: getY(p.value),
      };
    }, [chartData, hoverIndex]);

    const handleChartMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
      if (chartData.length === 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * chartWidth;
      // clamp to plotting area
      const clampedX = Math.max(leftPadding, Math.min(chartWidth - padding, x));
      const ratio = (clampedX - leftPadding) / (chartWidth - leftPadding - padding);
      const idx = Math.round(ratio * (chartData.length - 1));
      const safeIdx = Math.max(0, Math.min(chartData.length - 1, idx));
      setHoverIndex(safeIdx);
    };

    const handleChartMouseLeave = () => setHoverIndex(null);

    const attributes = apartment
      ? [
          {
            id: apartment.metrics.tempInterieurAttrId,
            title: 'Temp. intérieur',
            icon: 'home',
            value: apartment.metrics.tempInterieur,
            unit: '°C',
            accent: 'orange',
          },
          {
            id: apartment.metrics.tempConsigneAttrId,
            title: 'Temp. consigne',
            icon: 'tune',
            value: apartment.metrics.tempConsigne,
            unit: '°C',
            accent: 'yellow',
          },
          {
            id: apartment.metrics.humidityAttrId,
            title: 'Humidité',
            icon: 'water_drop',
            value: apartment.metrics.humidity,
            unit: '%',
            accent: 'purple',
          },
          {
            id: apartment.metrics.operatingStateAttrId,
            title: 'Vanne radiateur',
            icon: 'settings',
            value: apartment.metrics.position,
            unit: null,
            accent: 'emerald',
          },
        ].filter((attr) => attr.id !== null)
      : [];

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
        <div
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-1 min-h-0">
            {/* Sidebar */}
            {apartment && attributes.length > 0 && (
              <div className="w-64 border-r border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex flex-col">
                <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 truncate">{apartment.apartment.name}</p>
                </div>
                  <div className="flex-1 overflow-y-auto p-2">
                    {attributes.map((attr) => {
                      const isActive = attr.id === attributeId;
                      const accentColorClass =
                        attr.accent === 'orange'
                          ? 'bg-orange-400'
                          : attr.accent === 'yellow'
                            ? 'bg-yellow-400'
                            : attr.accent === 'purple'
                              ? 'bg-purple-400'
                              : attr.accent === 'emerald'
                                ? 'bg-emerald-400'
                                : 'bg-cyan-400';
                      return (
                        <button
                          key={attr.id}
                          onClick={() => {
                            if (attr.id) {
                              onAttributeChange(attr.id, attr.title);
                            }
                          }}
                          className={`w-full text-left p-3 rounded-lg mb-2 transition-colors relative ${
                            isActive
                              ? 'bg-primary/20 border border-primary/30 text-primary'
                              : 'bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700/50 hover:border-slate-300 dark:hover:border-slate-600'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="material-icons-outlined text-lg" style={{ fontSize: '20px' }}>
                              {attr.icon}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold truncate">{attr.title}</div>
                              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                {attr.value !== null
                                  ? typeof attr.value === 'number'
                                    ? `${attr.value.toFixed(1)}${attr.unit || ''}`
                                    : attr.value
                                  : 'N/A'}
                              </div>
                            </div>
                            {isActive && (
                              <span className="material-icons-outlined text-primary" style={{ fontSize: '18px' }}>
                                check_circle
                              </span>
                            )}
                          </div>
                          {/* Stroke line en bas */}
                          <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${accentColorClass} rounded-b-lg`} />
                        </button>
                      );
                    })}
                  </div>
              </div>
            )}

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">{title}</h2>
                  <div className="text-xs text-slate-500 mt-1">Attribut #{attributeId}</div>
                </div>
                <button onClick={onClose} className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                  <span className="material-icons-outlined">close</span>
                </button>
              </div>

          {/* Date Filters */}
          <div className="p-6 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <DateTimePicker
                value={dateFrom}
                onChange={handleDateFromChange}
              />
              <DateTimePicker
                value={dateTo}
                onChange={handleDateToChange}
              />
              <div className="flex items-end gap-2 flex-wrap">
                <button
                  onClick={() => {
                    const now = new Date();
                    const last48h = new Date(now.getTime() - 48 * 60 * 60 * 1000);
                    setDateFrom(formatLocalDateTimeForPicker(last48h));
                    setDateTo(formatLocalDateTimeForPicker(now));
                    setDateError(null);
                  }}
                  className="px-4 py-2 bg-primary/20 border border-primary/30 text-primary rounded-lg text-sm font-semibold hover:bg-primary/30 transition-colors whitespace-nowrap"
                >
                  Dernières 48h
                </button>
                <button
                  onClick={() => refetch()}
                  disabled={!!dateError || isExporting}
                  className={`px-4 py-2 border rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap ${
                    dateError || isExporting
                      ? 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                      : 'bg-slate-200 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600'
                  }`}
                >
                  <span className="material-icons-outlined" style={{ fontSize: '18px' }}>refresh</span>
                  Actualiser
                </button>
                {!isExporting && (
                  <button
                    onClick={handleExport}
                    disabled={!!dateError || !fromISO || !toISO}
                    className={`px-4 py-2 border rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap ${
                      dateError || !fromISO || !toISO
                        ? 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed opacity-60'
                        : 'bg-emerald-600/20 border-emerald-500/30 text-emerald-500 dark:text-emerald-400 hover:bg-emerald-600/30'
                    }`}
                  >
                    <span className="material-icons-outlined" style={{ fontSize: '18px' }}>download</span>
                    <span>Exporter</span>
                  </button>
                )}
              </div>
            </div>
            {isExporting && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-400">Génération du fichier CSV...</span>
                  <span className="text-xs text-slate-400">{Math.round(exportProgress)}%</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-primary h-full transition-all duration-300 ease-out rounded-full"
                    style={{ width: `${exportProgress}%` }}
                  />
                </div>
              </div>
            )}
            {dateError && (
              <div className="mt-3 p-3 bg-rose-500/10 border border-rose-500/20 rounded-lg">
                <div className="flex items-center gap-2 text-rose-400 text-sm">
                  <span className="material-icons-outlined" style={{ fontSize: '18px' }}>error</span>
                  <span>{dateError}</span>
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <span className="material-icons-outlined animate-spin text-primary text-4xl">progress_activity</span>
              </div>
            ) : chartData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400 gap-2">
                <span>Aucune donnée disponible</span>
                {(attribute?.currentValue != null && attribute.currentValue !== '') || attribute?.currentValueTs ? (
                  <div className="text-sm text-slate-500 dark:text-slate-400 text-center">
                    {attribute?.currentValue != null && attribute.currentValue !== '' && (
                      <div>Dernière valeur : <span className="font-semibold text-slate-700 dark:text-slate-300">{attribute.currentValue}{unit ? ` ${unit}` : ''}</span></div>
                    )}
                    {attribute?.currentValueTs && (
                      <div>Date : {formatParisDateTime(attribute.currentValueTs)}</div>
                    )}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Valeur actuelle</div>
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">
                      {chartData[chartData.length - 1]?.value.toFixed(2)}{unit ? ` ${unit}` : ''}
                    </div>
                  </div>
                  <div className="bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Minimum</div>
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">
                      {dataMin !== null ? `${dataMin.toFixed(2)}${unit ? ` ${unit}` : ''}` : '—'}
                    </div>
                  </div>
                  <div className="bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Maximum</div>
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">
                      {dataMax !== null ? `${dataMax.toFixed(2)}${unit ? ` ${unit}` : ''}` : '—'}
                    </div>
                  </div>
                </div>

                {/* Chart */}
                <div className="bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg p-6 overflow-x-auto">
                  <svg
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                    className="w-full h-auto min-w-[800px] cursor-crosshair"
                    onMouseMove={handleChartMouseMove}
                    onMouseLeave={handleChartMouseLeave}
                  >
                    {/* Grid lines and Y-axis labels */}
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                      const y = padding + ratio * (chartHeight - 2 * padding);
                      const value = minValue + (1 - ratio) * range;
                      const labelText = `${value.toFixed(1)}${unit ? ` ${unit}` : ''}`;
                      const estimatedTextWidth = labelText.length * 7;
                      const backgroundWidth = estimatedTextWidth + 8;
                      return (
                        <g key={ratio}>
                          <line x1={leftPadding} y1={y} x2={chartWidth - padding} y2={y} stroke={isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(100, 116, 139, 0.15)'} strokeWidth="1" />
                          <rect x={leftPadding - backgroundWidth - 5} y={y - 8} width={backgroundWidth} height="16" fill={isDark ? 'rgba(15, 23, 42, 0.85)' : 'rgba(248, 250, 252, 0.9)'} rx="2" />
                          <text x={leftPadding - 10} y={y + 4} fill={isDark ? 'rgb(226, 232, 240)' : 'rgb(51, 65, 85)'} fontSize="13" fontWeight="600" textAnchor="end">
                            {labelText}
                          </text>
                        </g>
                      );
                    })}

                    {/* X axis labels (dates) */}
                    {(() => {
                      if (chartData.length < 2) return null;
                      const indices = [
                        0,
                        Math.round((chartData.length - 1) * 0.25),
                        Math.round((chartData.length - 1) * 0.5),
                        Math.round((chartData.length - 1) * 0.75),
                        chartData.length - 1,
                      ];
                      const uniq = Array.from(new Set(indices)).sort((a, b) => a - b);
                      const y = chartHeight - padding + 22;
                      return (
                        <g>
                          <line x1={padding} y1={chartHeight - padding} x2={chartWidth - padding} y2={chartHeight - padding} stroke={isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(100, 116, 139, 0.25)'} strokeWidth="1" />
                          {uniq.map((i) => {
                            const p = chartData[i];
                            const x = getX(i);
                            const textAnchor = i === 0 ? 'start' : i === chartData.length - 1 ? 'end' : 'middle';
                            const label = formatParisDateTime(p.timestamp, {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                            });
                            return (
                              <g key={i}>
                                <line x1={x} y1={chartHeight - padding} x2={x} y2={chartHeight - padding + 6} stroke={isDark ? 'rgba(148, 163, 184, 0.25)' : 'rgba(100, 116, 139, 0.3)'} strokeWidth="1" />
                                <text x={x} y={y} fill={isDark ? 'rgb(148, 163, 184)' : 'rgb(100, 116, 139)'} fontSize="12" textAnchor={textAnchor}>
                                  {label}
                                </text>
                              </g>
                            );
                          })}
                        </g>
                      );
                    })()}

                    {/* Area fill */}
                    {pathData && (
                      <path
                        d={`${pathData} L ${getX(chartData.length - 1)} ${chartHeight - padding} L ${padding} ${chartHeight - padding} Z`}
                        fill="url(#gradient)"
                        opacity="0.2"
                      />
                    )}

                    {/* Line */}
                    {pathData && (
                      <path
                        d={pathData}
                        fill="none"
                        stroke="rgb(34, 211, 238)"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}

                    {/* Hover marker + tooltip */}
                    {hoveredPoint && (
                      <g>
                        <line
                          x1={hoveredPoint.x}
                          y1={padding}
                          x2={hoveredPoint.x}
                          y2={chartHeight - padding}
                          stroke="rgba(148, 163, 184, 0.35)"
                          strokeWidth="1"
                          strokeDasharray="4 4"
                        />
                        <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r={5} fill="rgb(34, 211, 238)" stroke={isDark ? 'rgb(15, 23, 42)' : 'rgb(248, 250, 252)'} strokeWidth="2" />

                        {(() => {
                          const outOfRange = hoveredPoint.value < minValue || hoveredPoint.value > maxValue;
                          const tooltipText = `${hoveredPoint.value.toFixed(2)}${unit ? ` ${unit}` : ''}${outOfRange ? ' (hors plage)' : ''} • ${hoveredPoint.label}`;
                          const boxWidth = 240;
                          const boxHeight = 34;
                          const margin = 8;
                          const x = Math.min(chartWidth - padding - boxWidth, Math.max(leftPadding, hoveredPoint.x - boxWidth / 2));
                          const y = Math.max(margin, hoveredPoint.y - boxHeight - 10);
                          return (
                            <g>
                              <rect x={x} y={y} width={boxWidth} height={boxHeight} rx={8} fill={isDark ? 'rgba(15, 23, 42, 0.92)' : 'rgba(248, 250, 252, 0.95)'} stroke={isDark ? 'rgba(148, 163, 184, 0.35)' : 'rgba(100, 116, 139, 0.4)'} />
                              <text x={x + 10} y={y + 22} fill={isDark ? 'rgb(226, 232, 240)' : 'rgb(51, 65, 85)'} fontSize="12">
                                {tooltipText}
                              </text>
                            </g>
                          );
                        })()}
                      </g>
                    )}

                    {/* Gradient definition */}
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="rgb(34, 211, 238)" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="rgb(34, 211, 238)" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>
            )}
          </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 text-slate-900 dark:bg-slate-900 dark:text-slate-100 h-screen flex overflow-hidden font-display">
      {/* Sidebar */}
      <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative bg-slate-50 dark:bg-slate-900">
        {/* Header */}
        <header className="h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-6 sm:px-8 z-20 shadow-sm sticky top-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <span className="material-icons-outlined" style={{ fontSize: '20px' }}>menu</span>
            </button>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
              Logements
            </h1>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-2"></div>
            <div className="flex items-center text-sm text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800/50 px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700/50">
              <span className="material-icons-outlined mr-2 text-slate-500" style={{ fontSize: '16px' }}>domain</span>
              {currentProject.title} • {currentProject.city || 'Paris'}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="hidden md:flex items-center px-3 py-1 bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-full text-xs font-medium text-slate-600 dark:text-slate-300">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse shadow-sm shadow-green-500/50"></span>
              Système en ligne
            </div>
            <div className="h-8 w-px bg-slate-200 dark:bg-slate-700 mx-1"></div>
            <ProjectLogo project={currentProject} size="md" className="rounded-lg" />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? <span className="material-icons-outlined">light_mode</span> : <span className="material-icons-outlined">dark_mode</span>}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900">
          {/* Statistics Cards */}
          {!isLoadingSupervision && !supervisionError && supervisionItems.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
              <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 opacity-5">
                  <span className="material-icons-outlined text-primary" style={{ fontSize: '120px' }}>home</span>
                </div>
                <div className="relative z-10">
                  <div className="text-xs text-slate-500 dark:text-slate-400 font-medium mb-3">Logements</div>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">{statistics.totalLogements}</div>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 opacity-5">
                  <span className="material-icons-outlined text-rose-400" style={{ fontSize: '120px' }}>router</span>
                </div>
                <div className="relative z-10">
                  <div className="text-xs text-slate-500 dark:text-slate-400 font-medium mb-3">Boxes hors ligne</div>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">{statistics.boxesOffline}</div>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 opacity-5">
                  <span className="material-icons-outlined text-orange-400" style={{ fontSize: '120px' }}>thermostat</span>
                </div>
                <div className="relative z-10">
                  <div className="text-xs text-slate-500 dark:text-slate-400 font-medium mb-3">Thermostats hors ligne</div>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">{statistics.thermostatsOffline}</div>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 opacity-5">
                  <span className="material-icons-outlined text-red-400" style={{ fontSize: '120px' }}>arrow_upward</span>
                </div>
                <div className="relative z-10">
                  <div className="text-xs text-slate-500 dark:text-slate-400 font-medium mb-3">Temp. max</div>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">
                    {statistics.maxTemp !== null ? (
                      <>
                        {statistics.maxTemp.toFixed(1)}
                        <span className="text-xl font-normal text-slate-500 dark:text-slate-400 ml-1">°C</span>
                        {statistics.maxTempOutOfRange && (
                          <span
                            className="text-base ml-1"
                            title="Valeur capteur hors plage plausible (7-36°C), affichage limité"
                          >
                            ⚠️
                          </span>
                        )}
                      </>
                    ) : (
                      'N/A'
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 opacity-5">
                  <span className="material-icons-outlined text-blue-400" style={{ fontSize: '120px' }}>arrow_downward</span>
                </div>
                <div className="relative z-10">
                  <div className="text-xs text-slate-500 dark:text-slate-400 font-medium mb-3">Temp. min</div>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">
                    {statistics.minTemp !== null ? (
                      <>
                        {statistics.minTemp.toFixed(1)}
                        <span className="text-xl font-normal text-slate-500 dark:text-slate-400 ml-1">°C</span>
                        {statistics.minTempOutOfRange && (
                          <span
                            className="text-base ml-1"
                            title="Valeur capteur hors plage plausible (7-36°C), affichage limité"
                          >
                            ⚠️
                          </span>
                        )}
                      </>
                    ) : (
                      'N/A'
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Controls */}
          <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-4 mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="min-w-[220px]">
                <select
                  value={selectedBuildingId ?? ''}
                  onChange={(e) => {
                    const next = e.target.value ? Number(e.target.value) : null;
                    setSelectedBuildingId(next);
                    setSelectedFloorId(null);
                  }}
                  className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {isLoadingBuildings ? (
                    <option value="">Chargement...</option>
                  ) : buildingsError ? (
                    <option value="">Erreur de chargement</option>
                  ) : buildings.length === 0 ? (
                    <option value="">Aucun bâtiment</option>
                  ) : (
                    buildings.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="min-w-[220px]">
                <select
                  value={selectedFloorId ?? ''}
                  onChange={(e) => setSelectedFloorId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={!selectedBuildingId || isLoadingFloors || !!floorsError}
                >
                  {!selectedBuildingId ? (
                    <option value="">Sélectionnez un bâtiment</option>
                  ) : isLoadingFloors ? (
                    <option value="">Chargement des étages...</option>
                  ) : floorsError ? (
                    <option value="">Erreur de chargement</option>
                  ) : floors.length === 0 ? (
                    <option value="">Aucun étage</option>
                  ) : (
                    floors.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => setStatusFilter('ONLINE')}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${
                  statusFilter === 'ONLINE'
                    ? 'bg-primary/20 border-primary/30 text-primary'
                    : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
              >
                En ligne
              </button>
              <button
                onClick={() => setStatusFilter('OFFLINE')}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${
                  statusFilter === 'OFFLINE'
                    ? 'bg-primary/20 border-primary/30 text-primary'
                    : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
              >
                Hors ligne
              </button>
              <button
                onClick={() => setStatusFilter('ALL')}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${
                  statusFilter === 'ALL'
                    ? 'bg-primary/20 border-primary/30 text-primary'
                    : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
              >
                Tout
              </button>
            </div>
          </div>

          {/* Listing grouped by floor */}
          <div className="space-y-6">
            {floorsError ? (
              <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-6 text-center text-slate-500 dark:text-slate-400">
                Erreur de chargement des étages.
              </div>
            ) : isLoadingFloors ? (
              <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-6 text-center text-slate-500 dark:text-slate-400">
                Chargement des étages...
              </div>
            ) : supervisionError ? (
              <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-6 text-center text-slate-500 dark:text-slate-400">
                Erreur de chargement des logements (supervision).
              </div>
            ) : isLoadingSupervision ? (
              <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-6 text-center text-slate-500 dark:text-slate-400">
                Chargement des logements...
              </div>
            ) : filteredFloors.length === 0 ? (
              <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-6 text-center text-slate-500 dark:text-slate-400">
                Aucun logement à afficher avec ce filtre.
              </div>
            ) : (
              filteredFloors.map((x) => (
                <div key={x.floor.id}>
                  {/* Floor header */}
                  <div className="flex items-center gap-3 mb-2 px-1">
                    <span className="material-icons-outlined text-slate-400 dark:text-slate-500" style={{ fontSize: '16px' }}>layers</span>
                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">{x.floor.name}</span>
                    <span className="text-xs text-slate-400 dark:text-slate-600">— {x.apartments.length} logement{x.apartments.length > 1 ? 's' : ''}</span>
                  </div>

                  {/* Column headers */}
                  <div className="hidden sm:grid grid-cols-12 gap-0 px-4 mb-1 text-[11px] text-slate-500 font-semibold uppercase tracking-wide">
                    <div className="col-span-3">Logement</div>
                    <div className="col-span-2">Box</div>
                    <div className="col-span-2">Thermostat</div>
                    <div className="col-span-3">Intérieur | Consigne</div>
                    <div className="col-span-1">Humidité</div>
                    <div className="col-span-1">Vanne</div>
                  </div>

                  {/* Rows */}
                  <div className="space-y-1">
                    {x.apartments.map((item) => (
                      <ApartmentRow key={item.apartment.id} item={item} />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Stream Modal - key pour forcer la réinitialisation complète quand l'attribut change */}
      {modalOpen && modalAttributeId && modalApartment && currentProject && (
        <StreamModal
          key={`${modalInstance}-${modalAttributeId}`}
          attributeId={modalAttributeId}
          title={modalTitle}
          apartment={modalApartment}
          projectId={currentProject.id}
          onClose={() => {
            setModalOpen(false);
            setModalApartment(null);
          }}
          onAttributeChange={(attrId, attrTitle) => {
            setModalAttributeId(attrId);
            setModalTitle(attrTitle);
            setModalInstance((v) => v + 1);
          }}
        />
      )}
    </div>
  );
}
